export type Lang = 'pt' | 'en';

// Each document carries its own button texts so a language version can be labelled in its own language.
export type CertificateDocument = { download: string; open: string; file: string };
export type Certificate = { id: string; name: string; subtitle: string; detail?: string; documents: CertificateDocument[] };

type Content = {
  htmlLang: string;
  title: string;
  description: string;
  languageSwitch: string;
  hero: { eyebrow: string; title: string[] };
  policy: {
    eyebrow: string;
    title: [string, string];
    version: string;
    date: string;
    lead: [string, string];
    body: string;
    commitments: { heading: string; text: string }[];
    closing: string;
    signatureNames: string;
    signatureRole: string;
  };
  certificates: {
    instruction: string;
    viewSeal: (name: string) => string;
    sealAlt: (name: string) => string;
    viewButton: string;
    items: Certificate[];
  };
  dialog: {
    close: string;
    unavailable: string;
    downloadSeal: string;
    sealFile: (id: string) => string;
  };
  footer: { tagline: string; credit: string };
};

const pt: Content = {
  htmlLang: 'pt-BR',
  title: 'Zschimmer & Schwarz Brasil | Qualidade e Certificações',
  description: 'A Zschimmer & Schwarz do Brasil reúne a Política do Sistema de Gestão Integrado (SGI) e as certificações ISO 9001, ISO 14001, ISO 45001, EcoVadis, AEO e Chem-MAP ZDHC Nível 3.',
  languageSwitch: 'Idioma',
  hero: { eyebrow: 'Nossos certificados', title: ['Qualidade,', 'segurança,', 'sustentabilidade', 'e política'] },
  policy: {
    eyebrow: 'Nosso compromisso',
    title: ['Política do Sistema', 'de Gestão Integrado'],
    version: 'MSGI 1.01 · Anexo 5 · Revisão 2',
    date: '15 de janeiro de 2024',
    lead: ['A ', ' assume que a qualidade dos produtos químicos que produz é o fator decisivo para a fidelização dos seus clientes e, portanto, a sustentabilidade da própria empresa.'],
    body: 'Entende também que, como parte intrínseca do negócio, deve desenvolver práticas sustentáveis e proporcionar ambientes de trabalho seguros e saudáveis. Tudo isso requer um Sistema de Gestão Integrado (SGI) cujos compromissos fundamentais, dentro do escopo definido, são:',
    commitments: [
      { heading: 'Produtos confiáveis', text: 'Fornecer produtos confiáveis em seus aspectos de funcionalidade.' },
      { heading: 'Foco no cliente', text: 'Produzir produtos que se adaptem às necessidades dos nossos clientes.' },
      { heading: 'Satisfação dos clientes', text: 'Priorizar a satisfação dos clientes.' },
      { heading: 'Prevenção da poluição', text: 'Proteger o meio ambiente, usando as melhores e mais adequadas práticas na produção de produtos químicos com vistas a prevenir a poluição.' },
      { heading: 'Recursos naturais e biodiversidade', text: 'Utilizar os recursos naturais de maneira sustentável, proteger a biodiversidade e os ecossistemas aquático e terrestre, mitigando os impactos ambientais adversos significativos identificados na nossa operação.' },
      { heading: 'Requisitos legais', text: 'Atender aos requisitos legais estabelecidos pelos órgãos regulamentadores da atividade.' },
      { heading: 'Cadeia de suprimentos', text: 'Influenciar nossa cadeia de suprimentos e prestadores de serviços para um envolvimento efetivo na proteção do meio ambiente e na saúde e segurança de seus colaboradores.' },
      { heading: 'Perigos e riscos', text: 'Eliminar os perigos quando possível e reduzir os riscos significativos identificados na nossa operação.' },
      { heading: 'Participação dos colaboradores', text: 'Garantir a consulta e participação dos colaboradores para identificar condições de trabalho inseguras em nossas instalações.' },
      { heading: 'Melhoria contínua', text: 'Melhorar continuamente a adequação, suficiência e eficácia do SGI, de forma a satisfazer as necessidades dos clientes, aumentar o desempenho ambiental e o desempenho de saúde e segurança ocupacional da organização.' },
    ],
    closing: 'Este compromisso é assumido por todos nós que integramos a Zschimmer & Schwarz do Brasil Ltda. como parte de nossa atuação individual e coletiva.',
    signatureNames: 'Julio D’Avila e Aline da Silva',
    signatureRole: 'Diretoria',
  },
  certificates: {
    instruction: 'Clique no certificado abaixo para visualizar ou baixar o documento',
    viewSeal: name => `Visualizar selo ${name}`,
    sealAlt: name => `Selo ${name}`,
    viewButton: 'Ver certificado',
    items: [
      { id: 'iso-9001', name: 'ISO 9001', subtitle: 'Sistema de Gestão da Qualidade', documents: [{ download: 'Baixar em português', open: 'Abrir em português em nova aba', file: 'zsb-iso-9001-2015-pt.pdf' }, { download: 'Download English version', open: 'Open English version in a new tab', file: 'zsb-iso-9001-2015-en.pdf' }] },
      { id: 'iso-14001', name: 'ISO 14001', subtitle: 'Gestão Ambiental', documents: [{ download: 'Baixar em português', open: 'Abrir em português em nova aba', file: 'zsb-iso-14001-2015-pt.pdf' }, { download: 'Download English version', open: 'Open English version in a new tab', file: 'zsb-iso-14001-2015-en.pdf' }] },
      { id: 'iso-45001', name: 'ISO 45001', subtitle: 'Saúde e Segurança Ocupacional', documents: [] },
      { id: 'ecovadis', name: 'EcoVadis', subtitle: 'Sustentabilidade', detail: 'Medalha Platinum 2026–2027', documents: [{ download: 'Baixar relatório de destaques', open: 'Abrir relatório de destaques em nova aba', file: 'zsb-ecovadis-platinum-2026-2027.pdf' }] },
      { id: 'chem-map', name: 'Eurofins – Chem-MAP', subtitle: 'Parceiro Aprovado', detail: 'ZDHC Nível 3', documents: [{ download: 'Baixar licença ZDHC', open: 'Abrir licença ZDHC em nova aba', file: 'zsb-chem-map-level-3-cmap26-036.pdf' }] },
      { id: 'aeo', name: 'AEO', subtitle: 'Operador Econômico Autorizado', detail: 'OEA-Conformidade Referência', documents: [{ download: 'Baixar certificado', open: 'Abrir certificado em nova aba', file: 'zsb-certificado-oea-conformidade-referencia.pdf' }] },
    ],
  },
  dialog: {
    close: 'Fechar visualização',
    unavailable: 'O documento oficial deste certificado ainda não está disponível nesta página.',
    downloadSeal: 'Baixar imagem do selo',
    sealFile: id => `${id}-selo.png`,
  },
  footer: { tagline: 'Química sob medida', credit: 'Orgulhosamente desenvolvido por' },
};

const en: Content = {
  htmlLang: 'en',
  title: 'Zschimmer & Schwarz do Brasil | Quality and Certifications',
  description: 'Read the Integrated Management System Policy of Zschimmer & Schwarz do Brasil and download its ISO 9001, ISO 14001, EcoVadis, AEO and ZDHC Chem-MAP Level 3 certificates.',
  languageSwitch: 'Language',
  hero: { eyebrow: 'Our certifications', title: ['Quality,', 'safety,', 'sustainability', 'and policy'] },
  policy: {
    eyebrow: 'Our commitment',
    title: ['Integrated Management', 'System Policy'],
    version: 'MSGI 1.01 · Annex 5 · Revision 2',
    date: 'January 15, 2024',
    lead: ['', ' recognizes that the quality of the chemical products it manufactures is the decisive factor in earning customer loyalty and, therefore, in ensuring the company’s sustainability.'],
    body: 'The company also understands that, as an intrinsic part of its business, it must develop sustainable practices and provide safe and healthy working environments. All of this requires an Integrated Management System (IMS) whose fundamental commitments, within its defined scope, are:',
    commitments: [
      { heading: 'Reliable products', text: 'Supply products that are reliable in terms of functionality.' },
      { heading: 'Customer focus', text: 'Manufacture products that adapt to our customers’ needs.' },
      { heading: 'Customer satisfaction', text: 'Prioritize customer satisfaction.' },
      { heading: 'Pollution prevention', text: 'Protect the environment by applying the best and most appropriate practices in the production of chemicals, with a view to preventing pollution.' },
      { heading: 'Natural resources and biodiversity', text: 'Use natural resources sustainably and protect biodiversity and aquatic and terrestrial ecosystems, mitigating the significant adverse environmental impacts identified in our operations.' },
      { heading: 'Legal compliance', text: 'Comply with the legal requirements established by the authorities that regulate our activities.' },
      { heading: 'Supply chain', text: 'Encourage our supply chain and service providers to engage effectively in protecting the environment and the health and safety of their employees.' },
      { heading: 'Hazards and risks', text: 'Eliminate hazards where possible and reduce the significant risks identified in our operations.' },
      { heading: 'Employee participation', text: 'Ensure that employees are consulted and take part in identifying unsafe working conditions at our facilities.' },
      { heading: 'Continual improvement', text: 'Continually improve the suitability, adequacy and effectiveness of the IMS in order to meet customers’ needs and enhance the organization’s environmental and occupational health and safety performance.' },
    ],
    closing: 'This commitment is shared by everyone at Zschimmer & Schwarz do Brasil Ltda. as part of our individual and collective responsibilities.',
    signatureNames: 'Julio D’Avila and Aline da Silva',
    signatureRole: 'Management',
  },
  certificates: {
    instruction: 'Click a certificate below to view or download it',
    viewSeal: name => `View the ${name} seal`,
    sealAlt: name => `${name} seal`,
    viewButton: 'View certificate',
    items: [
      { id: 'iso-9001', name: 'ISO 9001', subtitle: 'Quality Management System', documents: [{ download: 'Download English version', open: 'Open English version in a new tab', file: 'zsb-iso-9001-2015-en.pdf' }, { download: 'Download Portuguese version', open: 'Open Portuguese version in a new tab', file: 'zsb-iso-9001-2015-pt.pdf' }] },
      { id: 'iso-14001', name: 'ISO 14001', subtitle: 'Environmental Management System', documents: [{ download: 'Download English version', open: 'Open English version in a new tab', file: 'zsb-iso-14001-2015-en.pdf' }, { download: 'Download Portuguese version', open: 'Open Portuguese version in a new tab', file: 'zsb-iso-14001-2015-pt.pdf' }] },
      { id: 'iso-45001', name: 'ISO 45001', subtitle: 'Occupational Health and Safety', documents: [] },
      { id: 'ecovadis', name: 'EcoVadis', subtitle: 'Sustainability', detail: 'Platinum Medal 2026–2027', documents: [{ download: 'Download report (in Portuguese)', open: 'Open report in a new tab', file: 'zsb-ecovadis-platinum-2026-2027.pdf' }] },
      { id: 'chem-map', name: 'Eurofins – Chem-MAP', subtitle: 'Approved Partner', detail: 'ZDHC Level 3', documents: [{ download: 'Download certificate', open: 'Open certificate in a new tab', file: 'zsb-chem-map-level-3-cmap26-036.pdf' }] },
      { id: 'aeo', name: 'AEO', subtitle: 'Authorized Economic Operator', detail: 'AEO-Compliance Benchmark', documents: [{ download: 'Download certificate', open: 'Open certificate in a new tab', file: 'zsb-certificado-oea-conformidade-referencia.pdf' }] },
    ],
  },
  dialog: {
    close: 'Close',
    unavailable: 'The official certificate is not yet available for download on this page.',
    downloadSeal: 'Download seal image',
    sealFile: id => `${id}-seal.png`,
  },
  footer: { tagline: 'Tailor-made chemistry', credit: 'Proudly developed by' },
};

export const content: Record<Lang, Content> = { pt, en };
export type HomeContent = Content;
